"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

/**
 * Views for the `onenote-graph` capability. The view files live in
 * `../ms-graph/onenote-views` beside the other Graph views, mirroring the
 * server, where the tool sources sit under `ms-graph/tools/onenote/` and this
 * capability owns the registry.
 *
 * The four write tools have no custom view: the HITL approval card previews
 * the change and the result narrates as text.
 */
export const onenoteGraphToolRegistry = {
  onenote_graph_list_notebooks: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/onenote-views").then(
        ({ OnenoteListNotebooksToolView }) => ({
          default: OnenoteListNotebooksToolView,
        })
      )
    ),
  },
  onenote_graph_list_sections: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/onenote-views").then(
        ({ OnenoteListSectionsToolView }) => ({
          default: OnenoteListSectionsToolView,
        })
      )
    ),
  },
  onenote_graph_list_pages: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/onenote-views").then(
        ({ OnenoteListPagesToolView }) => ({
          default: OnenoteListPagesToolView,
        })
      )
    ),
  },
  onenote_graph_search_pages: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/onenote-views").then(
        ({ OnenoteSearchPagesToolView }) => ({
          default: OnenoteSearchPagesToolView,
        })
      )
    ),
  },
  onenote_graph_read_page: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/onenote-views").then(
        ({ OnenoteReadPageToolView }) => ({
          default: OnenoteReadPageToolView,
        })
      )
    ),
  },
  onenote_graph_read_resource: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/onenote-views").then(
        ({ OnenoteReadResourceToolView }) => ({
          default: OnenoteReadResourceToolView,
        })
      )
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant keyed by the progressive-disclosure inner-tool name
 * `onenote-graph_${toolName}`. Without this, a PD tool part resolves to no
 * view and falls back to `GenericToolView` — every notebook, section, page,
 * and search card in the transcript would degrade to raw JSON. The bare-name
 * entries above stay in the static tier, which `AllToolNames` still requires.
 * See ADR-057.
 */
export const onenoteGraphProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(onenoteGraphToolRegistry).map(([name, meta]) => [
    `onenote-graph_${name}`,
    meta,
  ])
);
